import { useState, useEffect } from "react";
import { fetchAvgCount } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the 7-business-day avg-count metrics.
 *
 * Fires once on mount. Cancels the in-flight request automatically if the
 * component unmounts before the response arrives (AbortController cleanup).
 *
 * All four averages are preserved as null when the backend returns null
 * (no qualifying rows in the 14-day Mon–Fri window, e.g. the POST cron
 * has not yet built up enough history). Callers should render "—" rather
 * than "0" or a delta percentage when any avg is null.
 *
 * The hook is intentionally separate from useHomepageData so the two
 * fetches are independent — an avg-count failure never blocks the live
 * current-value metrics from rendering, and vice versa.
 *
 * ── Avg semantics ─────────────────────────────────────────────────────────────
 *
 *   onlineMods      AVG(EntryCount WHERE AvgType='onlineMods') over Mon–Fri
 *                   rows in RTLS_avgCounts for the last 14 calendar days
 *                   (today excluded). Written by the avg-count POST route.
 *
 *   activePersonnel AVG(EntryCount WHERE AvgType='activePersonnel'), same window.
 *
 *   noncompliances  AVG(EntryCount WHERE AvgType='noncompliances'), same window.
 *
 *   modRolls        AVG(RollDuration) from RTLS_modRollTracker for Mon–Fri rows
 *                   in the same window. RollDuration is the stored per-roll
 *                   duration in minutes. null when no qualifying rows exist.
 *
 * @returns {{
 *   onlineMods:      number | null,
 *   activePersonnel: number | null,
 *   noncompliances:  number | null,
 *   modRolls:        number | null,
 *   meta: { windowStart: string | null, windowEnd: string | null, asOf: string | null },
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

// ── Module-level cache ────────────────────────────────────────────────────────
// Shared across all mounts in the same browser session so a navigate-away /
// navigate-back within the TTL window does not re-fetch.
// TTL matches useHomepageData (60 s) — both hooks refresh on the same cadence
// so their data stays temporally consistent when displayed side-by-side.
let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 60_000; // 1 minute

// ── Safe defaults ─────────────────────────────────────────────────────────────
// Returned during the loading phase so callers never receive undefined fields.
// All averages default to null (not 0) so the UI renders "—" while loading
// rather than implying a delta against a real zero baseline.
const DEFAULT_STATE = {
  onlineMods: null,
  activePersonnel: null,
  noncompliances: null,
  modRolls: null,
  meta: { windowStart: null, windowEnd: null, asOf: null },
};

const useAvgCount = () => {
  const [data, setData] = useState(_cache ?? DEFAULT_STATE);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // serve from cache, skip fetch

    const load = async () => {
      try {
        const result = await fetchAvgCount(controller.signal);
        _cache = result;
        _cacheTime = Date.now();
        setData(result);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load avg-count metrics.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { ...data, loading, error };
};

export default useAvgCount;
